// Shared human-readable labels for lead filters. Used by the filter bar (to
// render removable chips) and the export modal (to show, read-only, exactly
// which filters an export will apply) so the two never drift.

import type { LeadFilters } from "./useLeadFilters";

export const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "new", label: "New" },
  { value: "interested", label: "Interested" },
  { value: "follow_up", label: "Follow up" },
  { value: "not_interested", label: "Not interested" },
  { value: "converted", label: "Converted" },
  { value: "lost", label: "Lost" },
];

export const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "meta_ads", label: "Meta Ads" },
  { value: "website_form", label: "Website forms" },
  { value: "whatrite", label: "Whatrite" },
];

// Compact badge labels (lead table, dashboard chips). Kept alongside the long
// labels above so a new source is added in exactly one place instead of the
// many `source === "meta_ads" ? … : …` ternaries scattered across the UI.
const SOURCE_SHORT_LABELS: Record<string, string> = {
  meta_ads: "Meta",
  website_form: "Forms",
  whatrite: "Whatrite",
};

/** Long, filter-facing label (e.g. "Meta Ads"). Falls back to the raw value. */
export function sourceLabel(source: string | null | undefined): string {
  if (!source) return "Unknown";
  return SOURCE_OPTIONS.find((o) => o.value === source)?.label ?? source;
}

/** Compact badge label (e.g. "Meta"). Falls back to the raw value. */
export function sourceShortLabel(source: string | null | undefined): string {
  if (!source) return "Unknown";
  return SOURCE_SHORT_LABELS[source] ?? source;
}

export interface FilterDescriptor {
  key: string;
  label: string;
  value: string;
}

// A read-only description of every active filter except the date range (which
// the export modal owns and renders as an editable, capped control).
export function describeActiveFilters(
  filters: Pick<
    LeadFilters,
    "status" | "source" | "assignee" | "campaign" | "city" | "state" | "q"
  >,
  resolveAssigneeName: (id: string) => string
): FilterDescriptor[] {
  const out: FilterDescriptor[] = [];
  if (filters.status) {
    const opt = STATUS_OPTIONS.find((o) => o.value === filters.status);
    out.push({ key: "status", label: "Status", value: opt?.label ?? filters.status });
  }
  if (filters.source) {
    const opt = SOURCE_OPTIONS.find((o) => o.value === filters.source);
    out.push({ key: "source", label: "Source", value: opt?.label ?? filters.source });
  }
  if (filters.assignee) {
    out.push({ key: "assignee", label: "Assignee", value: resolveAssigneeName(filters.assignee) });
  }
  if (filters.campaign) {
    out.push({ key: "campaign", label: "Campaign", value: filters.campaign });
  }
  if (filters.city.length) {
    out.push({
      key: "city",
      label: filters.city.length > 1 ? "Cities" : "City",
      value: filters.city.join(", "),
    });
  }
  if (filters.state.length) {
    out.push({
      key: "state",
      label: filters.state.length > 1 ? "States" : "State",
      value: filters.state.join(", "),
    });
  }
  if (filters.q) {
    out.push({ key: "q", label: "Search", value: filters.q });
  }
  return out;
}
